/**
 * useGridLayers.ts
 * Hook for managing grid visualizations on the map
 */

import { useDispatch } from "react-redux";

import { Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { DEFAULT_STEP } from "@/constants/dataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { updateDatasetStyle } from "@/store/slice/datasetSlice";
import { Dataset, DatasetAttributeSchema, DatasetStyle } from "@/types/dataset";
import { hasCustomCategoryAssignments, isOrdinalType } from "@/utils/dataset";
import { addLayer, ensureMapStyleReady } from "@/utils/map";
import {
  addGridAutoRangeLayers,
  addGridCategoryLayers
} from "@/utils/map/grid";
import {
  addCentroidSource,
  addPolygonCustomCategoryLayers,
  addPolygonCustomRangeLayers,
  addPolygonSingleStyleLayers,
  addPolygonSource
} from "@/utils/map/polygon";

type UseGridLayersProps = {
  map: Map;
  prepareLayerStyles: (
    parsedPolygonData: FeatureCollection<Geometry>,
    dataset: Dataset,
    step: number
  ) => { updatedStyle: DatasetStyle };
  handleFeatureClick: (layerId: string) => void;
  handleMovePositionToLayer: (polygonData: FeatureCollection<Geometry>) => void;
  handleUpdateSourceWithRank: (value: {
    datasetId: string;
    style: DatasetStyle;
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    };
    sourceData?: FeatureCollection;
  }) => Promise<void>;
};
export const useGridLayers = ({
  map,
  handleFeatureClick,
  handleMovePositionToLayer,
  prepareLayerStyles,
  handleUpdateSourceWithRank
}: UseGridLayersProps) => {
  const dispatch = useDispatch();
  const {
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    isCustomMapVisualizationLegendEnabled
  } = useAppFeatures();

  const handleAddGridLayer = async (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>
  ) => {
    if (!polygonData) return;
    // Defer until the map's style is ready, so getSource() below is safe.
    const retry = () => handleAddGridLayer(dataset, polygonData);
    if (!ensureMapStyleReady(map, retry)) return;
    if (map.getSource(dataset.data.id)) return;

    const newLayer = {
      id: dataset.data.id,
      type: dataset.properties.spatialType,
      sublayers: {
        fill: `${dataset.data.id}-fill`,
        outline: `${dataset.data.id}-outline`,
        outlineSelected: `${dataset.data.id}-outline-selected`,
        label: `${dataset.data.id}-label`,
        rank: `${dataset.data.id}-rank`
      }
    };

    const { updatedStyle } = prepareLayerStyles(
      polygonData,
      dataset,
      dataset.style.colorSteps || DEFAULT_STEP
    );

    addPolygonSource(map, newLayer.id, polygonData);

    const gridCentroids = addCentroidSource(
      map,
      `${newLayer.id}-centroid`,
      polygonData
    );

    const isSingleStyle =
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "simple";
    // Custom Ordinal only "takes effect" once values are assigned; with an empty
    // config it falls through to the default ordinal/quantile coloring below.
    const isCustomCategory =
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "custom_ordinal" &&
      hasCustomCategoryAssignments(dataset.style.customCategoryConfig);
    // Custom Breaks: per-range fill + outline from the committed config.
    const isCustomRange =
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "custom_breaks" &&
      !!dataset.style.customRangeConfig;

    // Grid sublayers match the polygon ones, so the custom-legend builders are
    // shared — a restored legend then paints at creation time, not just on edit.
    if (isSingleStyle) {
      addPolygonSingleStyleLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle
      );
    } else if (isCustomCategory) {
      addPolygonCustomCategoryLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name
      );
    } else if (isCustomRange) {
      addPolygonCustomRangeLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name
      );
    } else {
      const isCategory = isOrdinalType(dataset.selectedCategory.data_type);

      addLayer({
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        layerData: newLayer,
        style: updatedStyle,
        selectedCategory: dataset.selectedCategory.attribute_name,
        isCategory,
        addCategoryFunc: addGridCategoryLayers,
        addAutoRangeFunc: addGridAutoRangeLayers
      });
    }

    handleUpdateSourceWithRank({
      datasetId: `${newLayer.id}-centroid`,
      style: updatedStyle,
      update: {},
      sourceData: gridCentroids ?? undefined
    });

    dispatch(
      updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
    );
    handleFeatureClick(newLayer.sublayers.fill);
    handleMovePositionToLayer(polygonData);
  };

  return {
    handleAddGridLayer
  };
};

export default useGridLayers;
